import dayjs from "dayjs";
import { Workbook } from "exceljs";
import { deburr, orderBy } from "lodash";
import { useCallback } from "react";
import { defineMessages, useIntl } from "react-intl";
import { getReportClientName } from "../utils";
import { SalesReportData } from "./useSalesReportData";

const INTEGER_NUM_FMT = "#,##0";

const m = defineMessages({
    clientNumber: { id: "SalesReportExcel.ClientNumber", defaultMessage: "Client Number" },
    client: { id: "SalesReportExcel.Client", defaultMessage: "Client" },
    country: { id: "SalesReportExcel.Country", defaultMessage: "Country" },
    province: { id: "SalesReportExcel.Province", defaultMessage: "Province/State" },
    city: { id: "SalesReportExcel.City", defaultMessage: "City" },
    onOrder: { id: "SalesReportExcel.OnOrder", defaultMessage: "On Order" },
    billed: { id: "SalesReportExcel.Billed", defaultMessage: "Billed" },
    period: { id: "SalesReportExcel.Period", defaultMessage: "Period" },
    totals: { id: "SalesReportExcel.Totals", defaultMessage: "Totals" },
    currentPeriod: { id: "SalesReportExcel.CurrentPeriod", defaultMessage: "Current" },
    previousPeriod: { id: "SalesReportExcel.PreviousPeriod", defaultMessage: "Previous" },
    worksheetName: { id: "SalesReportExcel.WorksheetName", defaultMessage: "Sales Report {yearPrevious}-{yearCurrent}" },
    filename: {
        id: "SalesReportExcel.Filename",
        defaultMessage: "Sales Report {monthPrevious} {yearPrevious} - {monthCurrent} {yearCurrent}",
    },
});

export const useSalesReportExcel = () => {
    const { formatMessage } = useIntl();

    const getClientAddressColumns = useCallback((clientCode: string, reportData: SalesReportData) => {
        const clientInformation = reportData.clientInformationByClientCode[clientCode];
        return [
            clientInformation?.country ?? "",
            clientInformation?.province ?? "",
            clientInformation?.city ?? "",
        ];
    }, []);

    const getCurrentPeriodRow = useCallback(
        (clientCode: string, reportData: SalesReportData) => {
            const monthlySales = reportData.monthlySalesByClientCode[clientCode];
            const incomingBillingAmount =
                reportData.incomingBillingAmountByClientCode[clientCode] || 0;
            const billedAmountAfterEndDate =
                reportData.billedAmountAfterEndDateByClientCode[clientCode] || 0;

            return [
                clientCode,
                getReportClientName(reportData.clientInformationByClientCode, clientCode),
                ...getClientAddressColumns(clientCode, reportData),
                incomingBillingAmount,
                billedAmountAfterEndDate,
                formatMessage(m.currentPeriod),
                ...reportData.monthsInOrder.map(
                    (month) => monthlySales?.statsByMonth[month]?.currentPeriodTotal ?? 0,
                ),
                monthlySales?.currentPeriodTotal ?? 0,
            ];
        },
        [formatMessage, getClientAddressColumns],
    );

    const getPreviousPeriodRow = useCallback(
        (clientCode: string, reportData: SalesReportData) => {
            const monthlySales = reportData.monthlySalesByClientCode[clientCode];

            return [
                clientCode,
                getReportClientName(reportData.clientInformationByClientCode, clientCode),
                ...getClientAddressColumns(clientCode, reportData),
                "",
                "",
                formatMessage(m.previousPeriod),
                ...reportData.monthsInOrder.map(
                    (month) => monthlySales?.statsByMonth[month]?.previousPeriodTotal ?? 0,
                ),
                monthlySales?.previousPeriodTotal ?? 0,
            ];
        },
        [formatMessage, getClientAddressColumns],
    );

    const getClientRows = useCallback(
        (clientCode: string, reportData: SalesReportData) => {
            return [
                getCurrentPeriodRow(clientCode, reportData),
                getPreviousPeriodRow(clientCode, reportData),
            ];
        },
        [getCurrentPeriodRow, getPreviousPeriodRow],
    );

    const getWorksheetName = useCallback(
        (reportData: SalesReportData) => {
            const yearPrevious = dayjs(reportData.previousPeriodStartDate).format("YYYY");
            const yearCurrent = dayjs(reportData.currentPeriodEndDate).format("YYYY");

            return formatMessage(m.worksheetName, {
                yearPrevious,
                yearCurrent,
            });
        },
        [formatMessage],
    );


    const addEntries = useCallback(
        (workbook: Workbook, reportData: SalesReportData) => {
            const worksheet = workbook.addWorksheet(getWorksheetName(reportData));

            const columnsWidths = [13, 35, 15, 15, 15, 10, 10, 12, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10];

            worksheet.columns = columnsWidths.map((width) => ({ width }));

            const headers = [
                formatMessage(m.clientNumber),
                formatMessage(m.client),
                formatMessage(m.country),
                formatMessage(m.province),
                formatMessage(m.city),
                formatMessage(m.onOrder),
                formatMessage(m.billed),
                formatMessage(m.period),
                ...reportData.monthsInOrder.map((month) =>
                    dayjs().month(month).format("MMM").toUpperCase(),
                ),
                formatMessage(m.totals),
            ];

            const headerRow = worksheet.addRow(headers);
            headerRow.eachCell((cell) => {
                cell.font = { bold: true };
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: "FFE0E0E0" },
                };
            });

            const orderedClientCodes = orderBy(reportData.clientCodes, (clientCode) =>
                deburr(getReportClientName(reportData.clientInformationByClientCode, clientCode).toLowerCase()),
            );

            const clientRowsData = orderedClientCodes.flatMap((clientCode) =>
                getClientRows(clientCode, reportData),
            );

            worksheet.addRows(clientRowsData);

            const firstMonthColumnIndex = 9;
            const OnOrderColumnIndex = 6;
            const BilledColumnIndex = 7;
            for (
                let rowIndex = headerRow.number + 1;
                rowIndex <= headerRow.number + clientRowsData.length;
                rowIndex++
            ) {
                const row = worksheet.getRow(rowIndex);
                row.getCell(OnOrderColumnIndex).numFmt = INTEGER_NUM_FMT;
                row.getCell(BilledColumnIndex).numFmt = INTEGER_NUM_FMT;
                for (let col = firstMonthColumnIndex; col <= headers.length; col++) {
                    row.getCell(col).numFmt = INTEGER_NUM_FMT;
                }
            }

            worksheet.autoFilter = {
                from: {
                    row: 1,
                    column: 1,
                },
                to: {
                    row: clientRowsData.length + 1,
                    column: headers.length,
                },
            };
        },
        [formatMessage, getClientRows, getWorksheetName],
    );

    const getFilename = useCallback(
        (reportData: SalesReportData) => {
            const yearPrevious = dayjs(reportData.previousPeriodStartDate).format("YYYY");
            const monthPrevious = dayjs(reportData.previousPeriodStartDate).format("MMMM");
            const yearCurrent = dayjs(reportData.currentPeriodEndDate).format("YYYY");
            const monthCurrent = dayjs(reportData.currentPeriodEndDate).format("MMMM");

            return formatMessage(m.filename, {
                monthPrevious,
                yearPrevious,
                monthCurrent,
                yearCurrent,
            });
        },
        [formatMessage],
    );

    const exportToExcelAndDownload = useCallback(
        async (reportData: SalesReportData) => {
            const filename = `${getFilename(reportData)}.xlsx`;

            const workbook = new Workbook();
            addEntries(workbook, reportData);

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.setAttribute("href", url);
            a.setAttribute("download", filename);
            a.click();
            window.URL.revokeObjectURL(url);
        },
        [addEntries, getFilename],
    );

    return { exportToExcelAndDownload };
};
